import type {ConfiguratorParameter, ConfiguratorParameterType} from "#template/configurator-parameters"
import type {ImageGenerator, MaterialGraph} from "@cm/material-nodes"
import type {CurveObjectData, MeshObjectData, ObjectData} from "#template/interfaces/object-data"
import type {AnyJsonValue, ExternalId, TransformValue} from "#template/value-types"
import type {ObjectPathPart} from "#template/interactions"
import type {TemplateData} from "#template/compile-context"
import type {ColorValue} from "#template/nodes/types"
import type {EvaluatedTemplateInput} from "#template/fundamental/evaluated-input"
import type {TemplateNode} from "#template/template-node/template-node"

export type EvaluatedMetaData = {[key: string]: string | number | boolean | AnyJsonValue}

export type InterfaceId = string
export type VariantId = string

export type DescriptorProps<V, T extends object = {}, O = TemplateNode> = {
    id: InterfaceId
    name: string
    isSetByTemplate?: boolean
    type: "input" | "output"
    value: V
    origin: O | undefined
    metaData?: EvaluatedMetaData
    affectedObjectPathParts?: ObjectPathPart[]
    affectedOutputIds?: string[]
} & T

export abstract class InterfaceDescriptor<V, T extends object = {}, O = TemplateNode> {
    constructor(public props: DescriptorProps<V, T, O>) {}
    clone(id: string, isSetByTemplate?: boolean): this {
        const Ctor = this.constructor as new (props: DescriptorProps<V, T, O>) => this
        return new Ctor({...this.props, id, isSetByTemplate})
    }
    toConfiguratorParameter(): ConfiguratorParameter {
        throw new Error("Method not implemented.")
    }
}

function createSimpleInfoClass<V>() {
    return class extends InterfaceDescriptor<V | null> {}
}

function createConfiguratorInfoClass<V>(configuratorType: ConfiguratorParameterType) {
    return class extends InterfaceDescriptor<V | null> {
        override toConfiguratorParameter(): ConfiguratorParameter {
            return {id: this.props.id, type: configuratorType, name: this.props.name, value: this.props.value}
        }
    }
}

export type VariantInfo = {
    id: VariantId
    name: string
    iconDataObjectId?: number
    iconColor?: ColorValue
    excludeFromPermutations?: boolean
}

export type ConfigInfoParameters = {
    variants: VariantInfo[]
    displayWithLabels: boolean
    order?: number
    sectionTitle?: string
}

export class ConfigInfo extends InterfaceDescriptor<VariantInfo | null, ConfigInfoParameters> {
    override toConfiguratorParameter(): ConfiguratorParameter {
        return {
            id: this.props.id,
            type: "config",
            name: this.props.name,
            value: this.props.value?.id,
            values: this.props.variants.map((v) => ({id: v.id, name: v.name})),
        }
    }
}

export class ObjectInfo extends createSimpleInfoClass<ObjectData>() {}
export class MeshInfo extends createSimpleInfoClass<MeshObjectData>() {}
export class CurveInfo extends createSimpleInfoClass<CurveObjectData>() {}
export class TemplateInfo extends createSimpleInfoClass<TemplateData>() {}
export class ImageInfo extends createSimpleInfoClass<ImageGenerator>() {}
export class NodesInfo extends createSimpleInfoClass<EvaluatedTemplateInput[]>() {}

export class MaterialInfo extends InterfaceDescriptor<MaterialGraph | null> {
    override toConfiguratorParameter(): ConfiguratorParameter {
        return {
            id: this.props.id,
            type: "material",
            name: this.props.name,
            value: this.props.value?.parameters?.materialLegacyId,
        }
    }
}

export class TransformInfo extends createConfiguratorInfoClass<TransformValue>("transform") {}
export class StringInfo extends createConfiguratorInfoClass<string>("string") {}
export class NumberInfo extends createConfiguratorInfoClass<number>("number") {}
export class BooleanInfo extends createConfiguratorInfoClass<boolean>("boolean") {}
export class JsonInfo extends createConfiguratorInfoClass<AnyJsonValue>("object") {}

export function wrapInterfaceId(prefix: ExternalId, id: InterfaceId): InterfaceId {
    return `${prefix}/${id}`
}

export function unwrapInterfaceId(id: InterfaceId): [ExternalId | null, InterfaceId] {
    const idx = id.lastIndexOf("/")
    if (idx === -1) return [null, id]
    return [id.slice(0, idx), id.slice(idx + 1)]
}

export function getInterfaceIdPrefix(id: InterfaceId): [ExternalId | null, ExternalId] {
    const idx = id.indexOf("/")
    if (idx === -1) return [null, id]
    return [id.slice(0, idx), id.slice(idx + 1)]
}

/////////////////////////////////////////////////////////////////
// Output-key helpers. Key format: `output-${type}-${outputId}`.

export function parseOutputTypeAndOutputIdFromOutputKey(outputKey: string): {outputType: string; outputId: string} {
    const match = outputKey.match(/^output-([^-]+)-(.+)$/)
    if (!match) throw new Error(`Invalid output key: ${outputKey}`)
    return {outputType: match[1], outputId: match[2]}
}

export function outputKeyFromOutputTypeAndOutputId(outputType: string, outputId: string): string {
    return `output-${outputType}-${outputId}`
}

export function isConfigInput(descriptor: InterfaceDescriptor<unknown, {}>): descriptor is ConfigInfo {
    return descriptor instanceof ConfigInfo && descriptor.props.type === "input"
}

export function isMaterialInput(descriptor: InterfaceDescriptor<unknown, {}>): descriptor is MaterialInfo {
    return descriptor instanceof MaterialInfo && descriptor.props.type === "input"
}

export function isBooleanInput(descriptor: InterfaceDescriptor<unknown, {}>): descriptor is BooleanInfo {
    return descriptor instanceof BooleanInfo && descriptor.props.type === "input"
}

export function isNumberInput(descriptor: InterfaceDescriptor<unknown, {}>): descriptor is NumberInfo {
    return descriptor instanceof NumberInfo && descriptor.props.type === "input"
}

export function isStringInput(descriptor: InterfaceDescriptor<unknown, {}>): descriptor is StringInfo {
    return descriptor instanceof StringInfo && descriptor.props.type === "input"
}

export function isTemplateInput(descriptor: InterfaceDescriptor<unknown, {}>): descriptor is StringInfo {
    return descriptor instanceof TemplateInfo && descriptor.props.type === "input"
}

export function getDescriptorsOfType<T extends InterfaceDescriptor<any, any>>(
    descriptors: InterfaceDescriptor<unknown, object>[],
    type: abstract new (...args: any[]) => T,
): T[] {
    return descriptors.filter((d): d is T => d instanceof type)
}
